// packages import
import { format } from 'date-fns'
import { Box, Button, CircularProgress, DialogActions, Typography } from '@mui/material'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
// component import
import { Alert } from '../../common/Alert'
import { CustomModal } from '../../common/CustomModal'
import TrustBalanceForm from './TrustBalanceForm'
/// others
import {
  BillStatus,
  BillType,
  CaseStatus,
  GetTrustBalanceSummaryQuery,
  useGetTrustBalanceSummaryLazyQuery,
  useUpdateCaseMutation
} from '../../../../generated'
import {
  CANCEL,
  CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE,
  FINAL_BILL_STATUSES,
  GRAPHQL_QUERY_POLICY,
  NOT_APPLICABLE,
  PRE_BILL_STATUSES,
  SOMETHING_WENT_WRONG
} from '../../../constants'
import { AppContext, CaseContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { CompleteCaseProps } from '../../../interfaceTypes'
import { capitalizeCase } from '../../../utils'
import { Link } from 'react-router-dom'
import palette from '../../../theme/palette'
import { formatStatus } from '../../../utils/billing'

export const CompleteCase: FC<CompleteCaseProps> = ({ refetch, caseRefetch }) => {
  const [trustBalanceSummary, setTrustBalanceSummary] = useState<
    GetTrustBalanceSummaryQuery['getTrustBalanceSummary'] | null
  >(null)
  const { isDeleteOpen, dispatch } = useContext(AppContext)
  const { currentCase } = useContext(CaseContext)

  const {
    title,
    id: currentCaseId,
    caseNumber,
    court,
    status,
    isConsultMatter,
    consultMatterNumber,
    completedDate,
    bills
  } = currentCase || {}
  const { name: courtName } = court || {}

  const preBillsStatues = PRE_BILL_STATUSES
  const finalBillsStatues = FINAL_BILL_STATUSES.filter(
    status => status !== BillStatus.Paid && status !== BillStatus.PrePaid
  )
  const unpaidBillStatuses = [...preBillsStatues, ...finalBillsStatues]

  const isCaseActive = useMemo(() => status === CaseStatus.Active, [status])
  const showTrustBalanceForm =
    isCaseActive && trustBalanceSummary && trustBalanceSummary.ioltaBalanceSummary?.balance > 0

  const [completeCase, { loading }] = useUpdateCaseMutation({
    variables: undefined,

    onError({ message }) {
      if (message === CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE) Alert.error(message)
      else Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      if (data) {
        const { updateCase } = data

        if (updateCase) {
          const { response, case: updatedCase } = updateCase
          const { status: responseStatus } = response || {}

          if (responseStatus === 200 && updatedCase) {
            const matterType = isConsultMatter ? 'Consult' : 'Matter'
            status === CaseStatus.Active
              ? Alert.success(`${matterType} Closed!`)
              : Alert.success(`${matterType} Opened!`)
            dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })
            refetch && refetch()
            caseRefetch && caseRefetch()
          }
        }
      }
    }
  })

  const [getTrustBalanceSummary, { loading: trustBalanceSummaryLoading }] = useGetTrustBalanceSummaryLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: data => {
      const { getTrustBalanceSummary } = data
      const { response } = getTrustBalanceSummary || {}

      if (response?.status === 200) {
        setTrustBalanceSummary(getTrustBalanceSummary)
      }
    }
  })

  const handleClose = () => dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })

  const handleDelete = async () => {
    if (currentCaseId && (caseNumber || consultMatterNumber)) {
      await completeCase({
        variables: {
          updateCaseInput: {
            id: currentCaseId,
            status: status === CaseStatus.Active ? CaseStatus.Completed : CaseStatus.Active,
            caseNumber: caseNumber || consultMatterNumber
          }
        }
      })
    }
  }

  useEffect(() => {
    return () => dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (currentCase?.id && isCaseActive && isDeleteOpen) {
      getTrustBalanceSummary({
        variables: {
          getTrustBalanceSummaryInput: {
            caseId: currentCase.id
          }
        }
      })
    }
  }, [currentCase?.id, getTrustBalanceSummary, isCaseActive, isDeleteOpen])

  const matterType = isConsultMatter ? 'Consult' : 'Matter'
  const modalTitle = status === CaseStatus.Active ? `Close ${matterType}` : `Open ${matterType}`

  return (
    <CustomModal title={modalTitle} isOpen={isDeleteOpen} handleClose={handleClose}>
      <Typography variant="body1">
        {status === CaseStatus.Active
          ? `You are about to mark the following ${isConsultMatter ? 'consult' : 'matter'} as closed:`
          : `You are about to mark the following ${isConsultMatter ? 'consult' : 'matter'} as open:`}
      </Typography>

      {/* Reopening clears the work completed date; reopening with it kept is "Mark Work Completed" instead. */}
      {!isCaseActive && completedDate && (
        <Typography variant="body1" mt={1}>
          Its work completed date (<strong>{format(new Date(Number(completedDate)), 'MMMM d, yyyy')}</strong>) will be
          removed.
        </Typography>
      )}

      <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
        {isConsultMatter && (
          <Typography variant="body1">
            <Typography component="span" fontWeight={700}>
              Consult Matter
            </Typography>
          </Typography>
        )}

        <Typography variant="body1">
          <Typography component="span" fontWeight={500}>
            Title:{' '}
          </Typography>{' '}
          {title || ''}
        </Typography>

        <Typography variant="body1">
          <Typography component="span" fontWeight={500}>
            Matter Number:{' '}
          </Typography>
          {caseNumber || consultMatterNumber || NOT_APPLICABLE}
        </Typography>

        <Typography variant="body1">
          <Typography component="span" fontWeight={500}>
            Court Name:{' '}
          </Typography>
          {capitalizeCase(courtName?.toLowerCase() || '') || NOT_APPLICABLE}
        </Typography>

        <Typography variant="body1">
          {bills && bills.filter(bill => unpaidBillStatuses.includes(bill?.status as BillStatus)).length > 0 ? (
            <>
              <Typography component="span" fontWeight={500}>
                Following are some unpaid bills for this matter:{' '}
              </Typography>
              {bills
                .filter(bill => unpaidBillStatuses.includes(bill?.status as BillStatus))
                .map((bill, index) =>
                  bill ? (
                    <Box key={bill.id || index}>
                      <Typography component="span">{`${index + 1}. `}</Typography>
                      {bill?.id && (
                        <Typography
                          color={palette.primary.main}
                          sx={{
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                          component={Link}
                          to={
                            bill?.type === BillType.Bill
                              ? `/billing/bill-detail/${bill.id}`
                              : `/billing/fund-request-detail/${bill.id}`
                          }
                        >{`${bill.type === BillType.Bill ? `INV-` : 'FR-'}${bill?.id}`}</Typography>
                      )}
                      {' - '}
                      <Typography component="span">{formatStatus(bill?.status as BillStatus)}</Typography>{' '}
                      <Typography component="span">{`(Total: $${bill?.amount})`}</Typography>
                    </Box>
                  ) : null
                )}
            </>
          ) : (
            <Typography component="span">No Unpaid Bills</Typography>
          )}
        </Typography>
      </Box>

      {!trustBalanceSummaryLoading && trustBalanceSummary && showTrustBalanceForm && (
        <TrustBalanceForm
          trustBalanceSummary={trustBalanceSummary}
          onClose={handleClose}
          onSuccess={() => {
            dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })
            refetch && refetch()
            caseRefetch && caseRefetch()
          }}
        />
      )}

      {!trustBalanceSummaryLoading && !showTrustBalanceForm && (
        <DialogActions sx={{ padding: '30px 0 0', flexWrap: 'wrap', gap: '10px' }}>
          <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
            {CANCEL}
          </Button>

          <Button
            disabled={loading}
            onClick={handleDelete}
            color="primary"
            variant="contained"
            endIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {status === CaseStatus.Active ? `Close ${matterType}` : `Open ${matterType}`}
          </Button>
        </DialogActions>
      )}
    </CustomModal>
  )
}
